import { useState, useRef } from "react";
import { clsx } from "clsx";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { Button as AriaButton, Checkbox as AriaCheckbox, CheckboxGroup, Dialog, DialogTrigger, Popover } from "react-aria-components";
import { _ } from "i18n";
import styles from "./styles.module.scss";

interface CheckboxBoxProps {
	isSelected: boolean;
}

interface Coach {
	id: number;
	name: string;
}

interface CoachReviewCoachSelectProps {
	coaches: Coach[];
	selectedIds: number[];
	onChange: (selectedIds: number[]) => void;
}

export const CoachReviewCoachSelect = ({ coaches, selectedIds, onChange }: CoachReviewCoachSelectProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [popoverWidth, setPopoverWidth] = useState<number>();
	const buttonRef = useRef<HTMLButtonElement>(null);

	const selected = new Set(selectedIds);
	const selectedCoaches = coaches.filter((coach) => selected.has(coach.id));
	const allSelected = coaches.length > 0 && selectedCoaches.length === coaches.length;
	const label = selectedCoaches.map((coach) => coach.name).join(", ");

	const Chevron = isOpen ? ChevronUp : ChevronDown;

	const handleOpen = (open: boolean) => {
		if (open) {
			setPopoverWidth(buttonRef.current?.offsetWidth);
		}
		setIsOpen(open);
	};

	return (
		<DialogTrigger isOpen={isOpen} onOpenChange={handleOpen}>
			<AriaButton type="button" className={styles["select__trigger"]} ref={buttonRef}>
				<span className={clsx(styles["select__value"], !label && styles["select__value--placeholder"])}>
					{label || _("game.coachReviews.coaches.placeholder")}
				</span>
				<Chevron size={20} aria-hidden />
			</AriaButton>

			<Popover placement="bottom start" offset={4} className={styles["select__popover"]} style={{ width: popoverWidth }}>
				<Dialog>
					<div className={styles["select__header"]}>
						<span className={styles["select__count"]}>{_("game.coachReviews.coaches.selected", { count: selectedCoaches.length })}</span>
						<AriaButton
							type="button"
							className={styles["select__clear"]}
							isDisabled={selectedIds.length === 0}
							onPress={() => onChange([])}
						>
							{_("game.coachReviews.coaches.clearAll")}
						</AriaButton>
					</div>
					<div className={styles["select__group"]}>
						<span className={styles["select__group-label"]}>{_("game.coachReviews.coaches.category")}</span>
						<AriaCheckbox
							isSelected={allSelected}
							onChange={(isSelected) => onChange(isSelected ? coaches.map((coach) => coach.id) : [])}
							className={styles["select__item"]}
						>
							{({ isSelected }) => (
								<>
									<span className={styles["select__item-label"]}>{_("game.coachReviews.coaches.all")}</span>
									<CheckboxBox isSelected={isSelected} />
								</>
							)}
						</AriaCheckbox>
						<CheckboxGroup value={selectedIds.map(String)} onChange={(values) => onChange(values.map(Number))}>
							{coaches.map((coach) => (
								<AriaCheckbox key={coach.id} value={String(coach.id)} className={styles["select__item"]}>
									{({ isSelected }) => (
										<>
											<span className={styles["select__item-label"]}>{coach.name}</span>
											<CheckboxBox isSelected={isSelected} />
										</>
									)}
								</AriaCheckbox>
							))}
						</CheckboxGroup>
					</div>
				</Dialog>
			</Popover>
		</DialogTrigger>
	);
};

const CheckboxBox = ({ isSelected }: CheckboxBoxProps) => {
	return (
		<span className={clsx(styles["select__box"], isSelected && styles["select__box--checked"])} aria-hidden>
			<Check size={16} strokeWidth={3} />
		</span>
	);
};
